import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import { toast } from 'react-toastify'
import {
  fetchScreenerDomains,
  fetchScreenerProfileCandidates,
  fetchScreenerProfileConfigGaps,
  fetchScreenerProfiles,
  fetchScreenerTargetSpecialties,
  saveScreenerProfile,
} from 'src/supportPortal/services/screenerProfileAdminService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const PERMISSION_FIELDS = [
  { key: 'canSubmitFinal', label: 'Submit final' },
  { key: 'canFlagEmergency', label: 'Flag emergency' },
  { key: 'canOverrideTriage', label: 'Override triage' },
  { key: 'canAssignDoctor', label: 'Assign doctor' },
  { key: 'canViewPastScreenings', label: 'View past screenings' },
  { key: 'canEditAfterSubmit', label: 'Edit after submit' },
]

const DEFAULT_PERMISSIONS = PERMISSION_FIELDS.reduce((acc, field) => {
  acc[field.key] = false
  return acc
}, {})

function getPractitionerId(candidate) {
  return candidate?.id || candidate?.practitionerId || candidate?._id || ''
}

function getDisplayName(item) {
  const name = [item?.firstName, item?.lastName].filter(Boolean).join(' ').trim()
  return name || item?.practitionerTitle || item?.title || item?.name || item?.email || item?.userName || item?.slug || item?.id || 'Unknown'
}

function getScreenerServiceDetails(candidate) {
  return candidate?.teacherUnEditableFields?.servicesApproved?.ROLE_SCREENER || {}
}

function normalizeSlugOption(item) {
  if (!item) return null
  const slug = item.slug || item.id || item._id || item.title || item.name
  if (!slug) return null
  return {
    slug,
    label: item.title || item.name || item.label || slug,
  }
}

function mapSlugsToOptions(slugs = [], options = []) {
  return (slugs || [])
    .filter(Boolean)
    .map((slug) => options.find((option) => option.slug === slug) || { slug, label: slug })
}

function splitTextValues(value) {
  if (!value) return []
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function buildInitialForm(candidate, profile) {
  const serviceDetails = getScreenerServiceDetails(candidate)
  return {
    practitionerId: getPractitionerId(candidate),
    supportedTargetSpecialtySlugs:
      profile?.supportedTargetSpecialtySlugs || serviceDetails?.targetSpecialitySlugs || [],
    supportedDomainSlugs: profile?.supportedDomainSlugs || [],
    clinicIdsText: (profile?.clinicIds || []).join(', '),
    enabledProtocolsText: (profile?.enabledProtocols || []).join(', '),
    rostering: Boolean(profile?.rostering),
    payoutsEnabled: Boolean(profile?.payoutsEnabled),
    enabled: profile?.enabled !== false,
    permissions: {
      ...DEFAULT_PERMISSIONS,
      ...(profile?.permissions || {}),
    },
  }
}

function profileStatus(candidate, profilesByPractitionerId) {
  const profile = profilesByPractitionerId.get(getPractitionerId(candidate))
  if (!profile) return { label: 'No profile', color: 'warning' }
  if (profile.enabled === false) return { label: 'Profile paused', color: 'default' }
  return { label: 'Profile ready', color: 'success' }
}

export default function ScreenerProfileAdminPanel({ mode = 'profiles' }) {
  const [candidates, setCandidates] = useState([])
  const [profiles, setProfiles] = useState([])
  const [gaps, setGaps] = useState([])
  const [targetSpecialties, setTargetSpecialties] = useState([])
  const [domains, setDomains] = useState([])
  const [selectedPractitionerId, setSelectedPractitionerId] = useState('')
  const [search, setSearch] = useState('')
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const targetSpecialtyOptions = useMemo(
    () => targetSpecialties.map(normalizeSlugOption).filter(Boolean),
    [targetSpecialties],
  )
  const domainOptions = useMemo(() => domains.map(normalizeSlugOption).filter(Boolean), [domains])
  const profilesByPractitionerId = useMemo(() => {
    const map = new Map()
    profiles.forEach((profile) => {
      if (profile?.practitionerId) {
        map.set(profile.practitionerId, profile)
      }
    })
    return map
  }, [profiles])

  const selectedCandidate = useMemo(
    () => candidates.find((candidate) => getPractitionerId(candidate) === selectedPractitionerId),
    [candidates, selectedPractitionerId],
  )
  const selectedProfile = selectedPractitionerId
    ? profilesByPractitionerId.get(selectedPractitionerId)
    : null

  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return candidates
    return candidates.filter((candidate) => {
      const haystack = [
        getDisplayName(candidate),
        candidate?.email,
        candidate?.mobile,
        candidate?.slug,
        candidate?.userId,
        getPractitionerId(candidate),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [candidates, search])

  async function loadData() {
    setLoading(true)
    setError('')
    try {
      if (mode === 'gaps') {
        setGaps(await fetchScreenerProfileConfigGaps())
      } else {
        const [candidateList, profileList, targetList, domainList] = await Promise.all([
          fetchScreenerProfileCandidates(),
          fetchScreenerProfiles(),
          fetchScreenerTargetSpecialties(),
          fetchScreenerDomains(),
        ])
        setCandidates(candidateList)
        setProfiles(profileList)
        setTargetSpecialties(targetList)
        setDomains(domainList)
        setSelectedPractitionerId((current) => current || getPractitionerId(candidateList[0]))
      }
    } catch (err) {
      setError('Unable to load screener profile data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  useEffect(() => {
    if (!selectedCandidate) {
      setForm(null)
      return
    }
    setForm(buildInitialForm(selectedCandidate, selectedProfile))
  }, [selectedCandidate, selectedProfile])

  function updateFormValue(key, value) {
    setForm((current) => ({ ...(current || {}), [key]: value }))
  }

  function updatePermission(key, value) {
    setForm((current) => ({
      ...(current || {}),
      permissions: {
        ...(current?.permissions || DEFAULT_PERMISSIONS),
        [key]: value,
      },
    }))
  }

  async function handleSave() {
    if (!form?.practitionerId) return
    setSaving(true)
    try {
      const response = await saveScreenerProfile({
        practitionerId: form.practitionerId,
        supportedDomainSlugs: form.supportedDomainSlugs || [],
        supportedTargetSpecialtySlugs: form.supportedTargetSpecialtySlugs || [],
        clinicIds: splitTextValues(form.clinicIdsText),
        enabledProtocols: splitTextValues(form.enabledProtocolsText),
        rostering: Boolean(form.rostering),
        payoutsEnabled: Boolean(form.payoutsEnabled),
        enabled: Boolean(form.enabled),
        permissions: form.permissions || DEFAULT_PERMISSIONS,
      })
      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const saved = getDataMapFromCommonResponse(response)?.['screener-profile']
        setProfiles((current) => {
          const next = current.filter((profile) => profile?.practitionerId !== saved?.practitionerId)
          return saved ? [saved, ...next] : current
        })
        toast.success('Screener profile saved')
      } else {
        const message = response?.data?.message
        toast.error(typeof message === 'string' ? message : 'Unable to save screener profile')
      }
    } catch (err) {
      toast.error('Unable to save screener profile')
    } finally {
      setSaving(false)
    }
  }

  if (mode === 'gaps') {
    return (
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              ROLE_SCREENER Missing Service Approval
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {gaps.length} users
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
            Refresh
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Practitioner</TableCell>
                <TableCell>Service State</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && gaps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>No gaps found.</TableCell>
                </TableRow>
              )}
              {!loading && gaps.map((gap) => (
                <TableRow key={`${gap.userId}-${gap.practitionerId || 'missing'}`}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {getDisplayName(gap)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gap.userName || gap.userId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{gap.email || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{gap.mobile || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{gap.practitionerTitle || gap.practitionerSlug || '-'}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {gap.kycStatus && <Chip size="small" label={gap.kycStatus} />}
                      {gap.practitionerEnabled === false && <Chip size="small" label="Paused" color="default" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={gap.serviceEnabled ? 'success' : 'warning'}
                      label={gap.serviceBlockPresent ? `enabled=${Boolean(gap.serviceEnabled)}` : 'missing block'}
                    />
                  </TableCell>
                  <TableCell>{gap.reason || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Screening Profile Editor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {candidates.length} eligible screeners
            </Typography>
          </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading || saving}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Search screeners"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Box>
            <Divider />
            <Box sx={{ minHeight: 420, maxHeight: 620, overflow: 'auto' }}>
              {loading && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading</Typography>
                </Stack>
              )}
              {!loading && filteredCandidates.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No screeners found.
                </Typography>
              )}
              <List dense disablePadding>
                {!loading && filteredCandidates.map((candidate) => {
                  const status = profileStatus(candidate, profilesByPractitionerId)
                  const serviceDetails = getScreenerServiceDetails(candidate)
                  return (
                    <ListItemButton
                      key={getPractitionerId(candidate)}
                      selected={getPractitionerId(candidate) === selectedPractitionerId}
                      onClick={() => setSelectedPractitionerId(getPractitionerId(candidate))}
                      sx={{ alignItems: 'flex-start', py: 1.25 }}
                    >
                      <ListItemText
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {getDisplayName(candidate)}
                            </Typography>
                            <Chip size="small" color={status.color} label={status.label} />
                          </Stack>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {candidate.email || candidate.mobile || candidate.slug || getPractitionerId(candidate)}
                            </Typography>
                            {(serviceDetails?.targetSpecialitySlugs || []).length > 0 && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {(serviceDetails.targetSpecialitySlugs || []).join(', ')}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItemButton>
                  )
                })}
              </List>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ borderRadius: 2, p: { xs: 2, md: 3 } }}>
            {!form ? (
              <Alert severity="info">No screener selected.</Alert>
            ) : (
              <Stack spacing={2.25}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {getDisplayName(selectedCandidate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedCandidate?.email || selectedCandidate?.mobile || selectedCandidate?.slug}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(form.enabled)}
                          onChange={(event) => updateFormValue('enabled', event.target.checked)}
                        />
                      }
                      label="Enabled"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(form.rostering)}
                          onChange={(event) => updateFormValue('rostering', event.target.checked)}
                        />
                      }
                      label="Rostering"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(form.payoutsEnabled)}
                          onChange={(event) => updateFormValue('payoutsEnabled', event.target.checked)}
                        />
                      }
                      label="Payouts section"
                    />
                  </Stack>
                </Stack>

                <Autocomplete
                  multiple
                  options={targetSpecialtyOptions}
                  value={mapSlugsToOptions(form.supportedTargetSpecialtySlugs, targetSpecialtyOptions)}
                  getOptionLabel={(option) => option?.label || option?.slug || ''}
                  isOptionEqualToValue={(option, value) => option.slug === value.slug}
                  onChange={(_, value) => updateFormValue('supportedTargetSpecialtySlugs', value.map((item) => item.slug))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index })
                      return <Chip key={key || option.slug} size="small" label={option.label} {...tagProps} />
                    })
                  }
                  renderInput={(params) => <TextField {...params} label="Target specialties" />}
                />

                <Autocomplete
                  multiple
                  options={domainOptions}
                  value={mapSlugsToOptions(form.supportedDomainSlugs, domainOptions)}
                  getOptionLabel={(option) => option?.label || option?.slug || ''}
                  isOptionEqualToValue={(option, value) => option.slug === value.slug}
                  onChange={(_, value) => updateFormValue('supportedDomainSlugs', value.map((item) => item.slug))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index })
                      return <Chip key={key || option.slug} size="small" label={option.label} {...tagProps} />
                    })
                  }
                  renderInput={(params) => <TextField {...params} label="Screening domains" />}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Clinic IDs"
                      value={form.clinicIdsText}
                      onChange={(event) => updateFormValue('clinicIdsText', event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Enabled protocols"
                      value={form.enabledProtocolsText}
                      onChange={(event) => updateFormValue('enabledProtocolsText', event.target.value)}
                    />
                  </Grid>
                </Grid>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Permissions
                  </Typography>
                  <FormGroup row>
                    {PERMISSION_FIELDS.map((field) => (
                      <FormControlLabel
                        key={field.key}
                        control={
                          <Checkbox
                            checked={Boolean(form.permissions?.[field.key])}
                            onChange={(event) => updatePermission(field.key, event.target.checked)}
                          />
                        }
                        label={field.label}
                      />
                    ))}
                  </FormGroup>
                </Box>

                <Divider />
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || loading}
                  >
                    Save Profile
                  </Button>
                </Stack>
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}
